import { FC, MouseEvent, Suspense, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import { useUserContext } from "../../../providers/userProvider";
import { regularPages, settingsPages } from "../../../router/router";
import { useNavigate } from "react-router-dom";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useCustomThemeContext } from "../../../providers/customThemeProvider";

const Navbar: FC = () => {
  const { theme, brand } = useCustomThemeContext();
  const navigate = useNavigate();
  const { user, userDetails, logout } = useUserContext();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElDropdown, setAnchorElDropdown] = useState<{
    [key: string]: null | HTMLElement;
  }>({});

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenDropdownMenu = (
    event: MouseEvent<HTMLElement>,
    key: string
  ) => {
    setAnchorElDropdown((prev) => ({ ...prev, [key]: event.currentTarget }));
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleCloseDropdownMenu = (key: string) => {
    setAnchorElDropdown((prev) => ({ ...prev, [key]: null }));
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const userRoles = userDetails?.roles || [];

  const filteredRegularPages = regularPages
    .map((page) => {
      const filteredChildren = page.children?.filter(
        (child) =>
          !child.permission ||
          userRoles.includes(child.permission.toLowerCase())
      );

      return {
        ...page,
        children:
          filteredChildren && filteredChildren.length > 0
            ? filteredChildren
            : undefined,
      };
    })
    .filter(
      (page) =>
        (!page.children || page.children.length > 0) &&
        (page.path || page.children) &&
        (!page.permission || userRoles.includes(page.permission.toLowerCase()))
    );

  const filteredSettingsPages = settingsPages.filter(
    (page) =>
      (page.path || page.children) &&
      (!page.permission || userRoles.includes(page.permission.toLowerCase()))
  );

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: theme.palette.navbarColor.main,
        borderBottom: 5,
        borderBottomStyle: "solid",
        borderColor: theme.palette.primary.main,
      }}
    >
      <Container>
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            src={brand.logoUrl}
            component="img"
            alt="Logo"
            sx={{
              mr: 1,
              display: { xs: "none", md: "flex" },
              height: 100,
              width: 200,
              paddingRight: 5,
              paddingTop: 2,
              paddingBottom: 2,
              objectFit: "contain",
            }}
            alignContent={"left"}
            onClick={() => navigate("")}
          ></Box>
          <Box onClick={() => navigate("")}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                mr: 2,
                display: { xs: "none", md: "flex" },
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "black",
                textDecoration: "none",
              }}
              textTransform={"uppercase"}
            >
              {brand.name.length > 15
                ? `${brand.name.substring(0, 15)}...`
                : brand.name}
            </Typography>
          </Box>
          <Box
            sx={{
              flexGrow: { xs: "none", md: 1 },
              display: { xs: "flex", md: "none" },
            }}
          >
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Suspense fallback={<div>Loading...</div>}>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "block", md: "none" },
                }}
              >
                {filteredRegularPages.map((page) =>
                  page.children ? (
                    <MenuItem
                      key={page.name}
                      onClick={(event) =>
                        handleOpenDropdownMenu(event, page.name)
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          textAlign: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography sx={{ textAlign: "center" }}>
                          {page.name}
                        </Typography>
                        {Boolean(anchorElDropdown[page.name]) ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </Box>
                      <Menu
                        anchorEl={anchorElDropdown[page.name]}
                        keepMounted
                        open={Boolean(anchorElDropdown[page.name])}
                        onClose={() => {
                          handleCloseDropdownMenu(page.name);
                          handleCloseNavMenu();
                        }}
                      >
                        {page.path && (
                          <MenuItem
                            key={page.name}
                            onClick={() => {
                              handleCloseDropdownMenu(page.name);
                              handleCloseNavMenu();
                              if (page.path) navigate(page.path);
                            }}
                          >
                            <Typography sx={{ textAlign: "center" }}>
                              {page.name}
                            </Typography>
                          </MenuItem>
                        )}
                        {page.children.map((child) => (
                          <MenuItem
                            key={child.name}
                            onClick={() => {
                              handleCloseDropdownMenu(page.name);
                              handleCloseNavMenu();
                              if (child.path) navigate(child.path);
                            }}
                          >
                            <Typography sx={{ textAlign: "center" }}>
                              {child.name}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Menu>
                    </MenuItem>
                  ) : (
                    <MenuItem
                      key={page.name}
                      onClick={() => {
                        handleCloseNavMenu();
                        if (page.path) navigate(page.path);
                      }}
                    >
                      <Typography sx={{ textAlign: "center" }}>
                        {page.name}
                      </Typography>
                    </MenuItem>
                  )
                )}
              </Menu>
            </Suspense>
          </Box>

          <Box
            src={brand.logoUrl}
            component="img"
            alt="Logo"
            sx={{
              mr: 1,
              display: { xs: "flex", md: "none" },
              height: 50,
              width: 100,
              paddingRight: 5,
              paddingTop: 2,
              paddingBottom: 2,
              objectFit: "contain",
            }}
            alignContent={"left"}
          ></Box>
          <Box onClick={() => navigate("/")}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                mr: 2,
                display: { xs: "flex", md: "none" },
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "black",
                textDecoration: "none",
              }}
              textTransform={"uppercase"}
            >
              {brand.name}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            {filteredRegularPages.map((page) =>
              page.children ? (
                <Box key={page.name} sx={{ flexGrow: 1 }}>
                  <MenuItem
                    onClick={(event) =>
                      handleOpenDropdownMenu(event, page.name)
                    }
                    sx={{
                      my: 2,
                      color: theme.palette.primary.main,
                      display: "block",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ textAlign: "center", width: "100%" }}>
                        {page.name.length > 15
                          ? `${page.name.substring(0, 15)}...`
                          : page.name}
                      </Typography>
                      {Boolean(anchorElDropdown[page.name]) ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </Box>
                  </MenuItem>
                  <Menu
                    anchorEl={anchorElDropdown[page.name]}
                    keepMounted
                    open={Boolean(anchorElDropdown[page.name])}
                    onClose={() => {
                      handleCloseDropdownMenu(page.name);
                      handleCloseNavMenu();
                    }}
                  >
                    {page.path && (
                      <MenuItem
                        key={page.name}
                        onClick={() => {
                          handleCloseDropdownMenu(page.name);
                          handleCloseNavMenu();
                          if (page.path) navigate(page.path);
                        }}
                      >
                        <Typography sx={{ textAlign: "center", width: "100%" }}>
                          {page.name}
                        </Typography>
                      </MenuItem>
                    )}
                    {page.children.map((child) => (
                      <MenuItem
                        key={child.name}
                        onClick={() => {
                          handleCloseDropdownMenu(page.name);
                          handleCloseNavMenu();
                          if (child.path) navigate(child.path);
                        }}
                      >
                        <Typography sx={{ textAlign: "center", width: "100%" }}>
                          {child.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ) : (
                <Box key={page.name} sx={{ flexGrow: 1 }}>
                  <MenuItem
                    onClick={() => {
                      handleCloseNavMenu();
                      if (page.path) navigate(page.path);
                    }}
                    sx={{
                      my: 2,
                      color: theme.palette.primary.main,
                      display: "block",
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ textAlign: "center", width: "100%" }}>
                      {page.name}
                    </Typography>
                  </MenuItem>
                </Box>
              )
            )}
          </Box>
          {!user && (
            <Box sx={{ flexGrow: 0 }}>
              <Button
                onClick={() => {
                  handleCloseUserMenu();
                  navigate("/login");
                }}
                variant="outlined"
                sx={{ my: 2, border: 2, display: "block" }}
              >
                Login
              </Button>
            </Box>
          )}
          {user && (
            <Box sx={{ flexGrow: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  border: 1,
                  borderColor: theme.palette.primary.main,
                  borderRadius: 10,
                  p: 1,
                }}
                onClick={handleOpenUserMenu}
              >
                {userDetails?.username && (
                  <Typography
                    variant="h6"
                    sx={{
                      mr: 2,
                      display: "flex",
                      alignItems: "center",
                      color: theme.palette.primary.main,
                    }}
                  >
                    {userDetails?.username}
                    {Boolean(anchorElUser) ? <ExpandLess /> : <ExpandMore />}
                  </Typography>
                )}
                <IconButton sx={{ p: 0 }}>
                  <Avatar
                    alt={"avatar of " + userDetails?.username}
                    src={userDetails?.profileImageUrl}
                  />
                </IconButton>
              </Box>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {filteredSettingsPages.map((page) => (
                  <MenuItem
                    key={page.name}
                    onClick={() => {
                      if (page.name.toLowerCase() === "logout") {
                        handleCloseUserMenu();
                        handleLogout();
                      } else {
                        handleCloseUserMenu();
                        if (page.path) navigate(page.path);
                      }
                    }}
                  >
                    {page.name}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
